export type StatementStatus = 'historical' | 'draft' | 'approved' | 'sent' | 'invoice_received' | 'booked' | 'paid' | 'reversed';

const money = (v: string | null, symbol: string) =>
  v === null ? '-' : `${Number(v) < 0 ? '-' : ''}${symbol}${Math.abs(Number(v)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const usd = (v: string | null) => money(v, '$');
export const eur = (v: string | null) => money(v, 'EUR ');
export const period = (m: { year: number; month: number }) => `${m.year}-${String(m.month).padStart(2, '0')}`;
export const monthKey = (m: { year: number; month: number }) => m.year * 100 + m.month;

export const STATEMENT_STATUS: Record<StatementStatus, { label: string; className: string }> = {
  historical: { label: 'Historical', className: 'text-slate-500' },
  draft: { label: 'Draft', className: 'text-amber-700 dark:text-amber-400' },
  approved: { label: 'Approved', className: 'text-sky-700 dark:text-sky-400' },
  sent: { label: 'Sent', className: 'text-sky-700 dark:text-sky-400' },
  invoice_received: { label: 'Invoice received', className: 'text-sky-700 dark:text-sky-400' },
  booked: { label: 'Booked', className: 'text-emerald-700 dark:text-emerald-400' },
  paid: { label: 'Paid', className: 'text-emerald-700 dark:text-emerald-400' },
  reversed: { label: 'Reversed', className: 'text-red-700 line-through dark:text-red-400' },
};

export type DeliveryStatus = 'queued' | 'sending' | 'unconfirmed' | 'sent' | 'failed' | 'bounced' | 'complained';

/** Latest mail of a statement (STE-10); failures, bounces and complaints are what needs attention. */
export const DELIVERY_STATUS: Record<DeliveryStatus, { label: string; className: string; problem: boolean }> = {
  queued: { label: 'Mail queued', className: 'text-amber-700 dark:text-amber-400', problem: false },
  sending: { label: 'Sending', className: 'text-sky-700 dark:text-sky-400', problem: false },
  unconfirmed: { label: 'Outcome unknown', className: 'text-amber-700 dark:text-amber-400', problem: false },
  sent: { label: 'Mailed', className: 'text-emerald-700 dark:text-emerald-400', problem: false },
  failed: { label: 'Mail failed', className: 'font-semibold text-red-700 dark:text-red-400', problem: true },
  bounced: { label: 'Bounced', className: 'font-semibold text-red-700 dark:text-red-400', problem: true },
  complained: { label: 'Complaint', className: 'font-semibold text-red-700 dark:text-red-400', problem: true },
};

export type VoucherStatus = 'pending' | 'creating' | 'created' | 'booked' | 'failed' | 'void_required';

/** A Lexware voucher of this system (STE-11). */
export const VOUCHER_STATUS: Record<VoucherStatus, { label: string; className: string }> = {
  pending: { label: 'To be booked', className: 'text-amber-700 dark:text-amber-400' },
  creating: { label: 'Being created (outcome checked)', className: 'text-amber-700 dark:text-amber-400' },
  created: { label: 'Created, files pending', className: 'text-sky-700 dark:text-sky-400' },
  booked: { label: 'Booked in Lexware', className: 'text-emerald-700 dark:text-emerald-400' },
  failed: { label: 'Refused by Lexware', className: 'font-semibold text-red-700 dark:text-red-400' },
  void_required: { label: 'Void in Lexware by hand', className: 'font-semibold text-red-700 dark:text-red-400' },
};

/** Why something is not matched or not booked yet. */
export const REASON_TEXT: Record<string, string> = {
  unknown_sender: 'Sender is no partner contact',
  sender_not_authenticated: 'Sender not authenticated by the mail server (no DMARC or aligned DKIM pass)',
  pdf_too_large: 'PDF larger than 5 MB',
  no_statement_waiting: 'No statement of the partner waits for an invoice',
  several_statements_waiting: 'Several statements wait; the amount did not decide',
  amount_not_matched: 'Amount matches no payout',
  no_amount_found: 'No amount found in the PDF',
  no_text_in_pdf: 'The PDF has no text (scan)',
  unassigned: 'Taken off its statement by hand',
  statements_not_approved: 'Statements of the payment are not approved yet',
  notice_missing: 'No evidence of the Steam payment: enter it from the bank transaction or upload Valve\'s notice on the Payments page',
  payment_before_book_from: 'Payout before the first date booked in Lexware',
  invoice_details_missing: 'Invoice number or date missing',
  invoice_amount_missing: 'Invoice amount missing',
  invoice_amount_zero: 'Invoice amount is zero',
  difference_not_accepted: 'Amount differs from the expected total; accept the difference first',
  invoice_not_accepted: 'Invoice from the mailbox not accepted yet (statement page)',
  purchase_category_missing: 'No Lexware posting category for the partner\'s invoice VAT (Settings > Bookkeeping)',
  partner_contact_missing: 'Partner contact not in Lexware yet (next round)',
};

export const reasonText = (code: string | null | undefined) => (code ? (REASON_TEXT[code] ?? code) : '');

export type ReconciliationStatus = 'matched' | 'mismatch' | 'report_missing' | 'overridden';

export const RECONCILIATION_STATUS: Record<ReconciliationStatus, { label: string; className: string }> = {
  matched: { label: 'Matched', className: 'text-emerald-700 dark:text-emerald-400' },
  mismatch: { label: 'Mismatch', className: 'font-semibold text-red-700 dark:text-red-400' },
  report_missing: { label: 'Report missing', className: 'font-semibold text-amber-700 dark:text-amber-400' },
  overridden: { label: 'Overridden', className: 'text-sky-700 dark:text-sky-400' },
};
